/**
 * Utility functions for common operations.
 *
 * Provides helper functions used throughout the application, including
 * className merging utilities for Tailwind CSS.
 *
 * @module utils
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Builds a URL query string from a record of parameters.
 *
 * Automatically filters out undefined values and converts booleans to strings.
 * Returns an empty string if no valid parameters are provided.
 *
 * @param params - Record of query parameters (string, boolean, or undefined)
 * @returns Query string without the leading `?` (e.g., "level=A1&goal=exam")
 *
 * @example
 * ```typescript
 * const queryString = buildQueryString({
 *   level: 'A1',
 *   goal: 'exam',
 *   isOnline: true,
 *   location: undefined, // Will be filtered out
 * });
 * // Returns: "level=A1&goal=exam&isOnline=true"
 * ```
 */
export function buildQueryString(
  params: Record<string, string | boolean | undefined>,
): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    // Skip undefined values
    if (value === undefined) {
      continue;
    }

    // Convert boolean to string, otherwise use value as-is
    const stringValue = typeof value === 'boolean' ? value.toString() : value;
    searchParams.append(key, stringValue);
  }

  return searchParams.toString();
}

